import React, { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import Flag from "react-world-flags";
import AOS from "aos";
import "aos/dist/aos.css";
import {
  FaCheckCircle,
  FaDownload,
  FaPhone,
  FaLock,
  FaChevronDown,
  FaCalendarCheck,
} from "react-icons/fa";

/* ================= BRAND ================= */
const PRIMARY_GRADIENT =
  "bg-gradient-to-r from-primary-soft via-primary to-primary-soft bg-clip-text text-transparent animate-gradient";

/* ================= FAQ DATA ================= */
const FAQ_DATA = [
  {
    question: "Do I need prior Spanish knowledge to start?",
    answer:
      "No prior knowledge is required for A1. Our beginner course starts from the basics, including pronunciation, everyday vocabulary, and simple sentence structures.",
  },
  {
    question: "Which exams does this course prepare me for?",
    answer:
      "Our Spanish programs are aligned with DELE (Diplomas de Español como Lengua Extranjera), officially recognized by the Spanish Ministry of Education and accepted worldwide.",
  },
  {
    question: "How long does it take to complete each level?",
    answer:
      "A1 and A2 typically take 2–3 months, B1 around 3 months, and B2–C2 may take 4 months depending on learning pace and batch type.",
  },
  {
    question: "Are the classes online or offline?",
    answer:
      "We offer both online live classes and offline classroom sessions. Online classes include live interaction, recordings, and structured practice.",
  },
  {
    question: "Will I get a certificate after completion?",
    answer:
      "Yes, you will receive a course completion certificate from The Lingua Alliance along with full guidance for official DELE certification exams.",
  },
  {
    question: "What if I miss a class?",
    answer:
      "All sessions are recorded and available for lifetime access. Make-up classes are also available whenever possible.",
  },
  {
    question: "Is Spanish easy to learn?",
    answer:
      "Spanish is considered one of the easiest languages to learn for English speakers due to clear pronunciation, consistent grammar, and familiar vocabulary.",
  },
];

/* ================= COMPONENT ================= */
export default function Spanish() {
  const formRef = useRef(null);
  const [expandedFaq, setExpandedFaq] = useState(null);

  const toggleFaq = (index) => {
    setExpandedFaq(expandedFaq === index ? null : index);
  };

  /* Initialize AOS */
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      offset: 100,
    });
  }, []);

  /* Sticky stop before footer */
  useEffect(() => {
    const handler = () => {
      const form = formRef.current;
      const footer = document.getElementById("page-footer");
      if (!form || !footer) return;

      const scrollY = window.pageYOffset;
      const footerTop = footer.getBoundingClientRect().top + scrollY;
      const formHeight = form.offsetHeight;
      const stopPoint = footerTop - formHeight - 24;

      if (scrollY >= stopPoint) {
        form.style.position = "absolute";
        form.style.top = `${stopPoint - form.parentElement.offsetTop}px`;
      } else {
        form.style.position = "sticky";
        form.style.top = "24px";
      }
    };

    window.addEventListener("scroll", handler);
    window.addEventListener("resize", handler);
    handler();

    return () => {
      window.removeEventListener("scroll", handler);
      window.removeEventListener("resize", handler);
    };
  }, []);

  return (
    <div className="min-h-screen bg-slate-50 text-gray-900">
      {/* ================= HERO ================= */}
      <header className="relative min-h-[550px] overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{
            backgroundImage:
              "url('https://images.unsplash.com/photo-1509840841025-9088ba78a826?w=1600&q=80')",
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-black/90 via-black/70 to-black/50" />

        <div className="relative max-w-7xl mx-auto px-6 py-16 text-white">
          <div className="max-w-3xl space-y-6">
            <div className="text-sm text-white/80">
              <Link to="/" className="hover:text-white">
                Home
              </Link>{" "}
              / Language Programs / <strong>Spanish</strong>
            </div>

            <div className="flex items-center gap-3">
              <Flag code="ES" style={{ width: 40, height: 28 }} />
              <span className="text-xs uppercase tracking-widest text-white/80">
                Learn Spanish with The Lingua Alliance
              </span>
            </div>

            <h1
              className={`text-4xl md:text-5xl pb-2 font-extrabold text-white`}
            >
              DELE-Focused Spanish Learning Tracks
            </h1>

            <p className="text-lg text-white/90 max-w-2xl">
              Step-by-step Spanish programs designed to take you from beginner
              to fluent professional with globally recognized DELE standards.
            </p>

            <div className="flex flex-wrap gap-4 pt-3">
              <button className="inline-flex items-center gap-2 px-6 py-3 bg-white text-primary font-semibold rounded-lg hover:bg-gray-100 transition-all hover:shadow-lg hover:-translate-y-0.5">
                <FaDownload className="text-lg" />
                Download Brochure
              </button>
              <button className="inline-flex items-center gap-2 px-6 py-3 bg-gradient-to-r from-primary-soft via-primary to-primary-soft text-white font-semibold rounded-lg hover:shadow-xl transition-all hover:-translate-y-0.5">
                <FaCalendarCheck className="text-lg" />
                Book Free Demo
              </button>
            </div>
          </div>
        </div>
      </header>

      <section className="py-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div
              className="group relative overflow-hidden text-center py-6 px-4 bg-gradient-to-br from-[#1f2c48] via-[#2e3c58] to-[#1f2c48] backdrop-blur-sm rounded-2xl border border-primary/20 shadow-md hover:shadow-lg hover:-translate-y-1 hover:bg-white/10 transition"
              data-aos="fade-up"
              data-aos-delay="0"
            >
              <span className="pointer-events-none absolute inset-0 translate-y-10 bg-gradient-to-br from-primary/20 to-primary-soft/15 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-90" />
              <div className="relative">
                <div className="text-3xl font-bold text-primary mb-1">11+</div>
                <div className="text-xs text-white/90 uppercase tracking-wide">
                  Years of Expertise
                </div>
              </div>
            </div>
            <div
              className="group relative overflow-hidden text-center py-6 px-4 bg-gradient-to-br from-[#1f2c48] via-[#2e3c58] to-[#1f2c48] backdrop-blur-sm rounded-2xl border border-primary/20 shadow-md hover:shadow-lg hover:-translate-y-1 hover:bg-white/10 transition"
              data-aos="fade-up"
              data-aos-delay="100"
            >
              <span className="pointer-events-none absolute inset-0 translate-y-10 bg-gradient-to-br from-primary/20 to-primary-soft/15 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-90" />
              <div className="relative">
                <div className="text-3xl font-bold text-primary mb-1">
                  4,000+
                </div>
                <div className="text-xs text-white/90 uppercase tracking-wide">
                  Students Across Globe
                </div>
              </div>
            </div>
            <div
              className="group relative overflow-hidden text-center py-6 px-4 bg-gradient-to-br from-[#1f2c48] via-[#2e3c58] to-[#1f2c48] backdrop-blur-sm rounded-2xl border border-primary/20 shadow-md hover:shadow-lg hover:-translate-y-1 hover:bg-white/10 transition"
              data-aos="fade-up"
              data-aos-delay="200"
            >
              <span className="pointer-events-none absolute inset-0 translate-y-10 bg-gradient-to-br from-primary/20 to-primary-soft/15 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-90" />
              <div className="relative">
                <div className="text-3xl font-bold text-primary mb-1">✓</div>
                <div className="text-xs text-white/90 uppercase tracking-wide">
                  Certified Experts
                </div>
              </div>
            </div>
            <div
              className="group relative overflow-hidden text-center py-6 px-4 bg-gradient-to-br from-[#1f2c48] via-[#2e3c58] to-[#1f2c48] backdrop-blur-sm rounded-2xl border border-primary/20 shadow-md hover:shadow-lg hover:-translate-y-1 hover:bg-white/10 transition"
              data-aos="fade-up"
              data-aos-delay="300"
            >
              <span className="pointer-events-none absolute inset-0 translate-y-10 bg-gradient-to-br from-primary/20 to-primary-soft/15 opacity-0 transition duration-300 group-hover:translate-y-0 group-hover:opacity-90" />
              <div className="relative">
                <div className="text-3xl font-bold text-primary mb-1">
                  All Ages
                </div>
                <div className="text-xs text-white/90 uppercase tracking-wide">
                  Programs Available
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ================= MAIN GRID ================= */}
      <div className="max-w-7xl mx-auto px-6 py-14 grid lg:grid-cols-[1fr_360px] gap-12">
        {/* ================= LEFT CONTENT ================= */}
        <main className="space-y-16">
          {/* COURSE LEVELS */}
          <section>
            <h2 className={`text-3xl font-bold mb-6 ${PRIMARY_GRADIENT}`}>
              Spanish Language Program (DELE Aligned)
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              <LevelItem
                title="A1 (Beginner) | 40 Hours"
                text="Lay a solid foundation with basic greetings, simple sentence patterns, and essential everyday vocabulary."
              />
              <LevelItem
                title="A2 (Elementary) | 40 Hours"
                text="Gain confidence using familiar expressions, holding short conversations, and applying fundamental grammar."
              />
              <LevelItem
                title="B1 (Intermediate) | 60 Hours"
                text="Engage in everyday discussions, share your views clearly, and strengthen overall comprehension skills."
              />
              <LevelItem
                title="B2 (Upper-Intermediate) | 80 Hours"
                text="Understand detailed texts, discuss abstract ideas, and communicate naturally with native speakers."
              />
              <LevelItem
                title="C1 (Advanced) | 80 Hours"
                text="Speak fluently on academic or professional topics, grasp subtle meanings, and write with sophistication."
              />
              <LevelItem
                title="C2 (Mastery) | 80 Hours"
                text="Achieve near-native command, interpret literature, master advanced grammar, and express cultural depth."
              />
            </div>
          </section>

          {/* UNIQUE */}
          <section>
            <h2 className={`text-3xl font-bold mb-6 ${PRIMARY_GRADIENT}`}>
              What Makes Our Spanish Program Unique
            </h2>

            <ul className="grid md:grid-cols-2 gap-4 text-gray-700">
              <ListItem text="DELE-aligned curriculum with exam focus" />
              <ListItem text="Highly experienced Spanish language trainers" />
              <ListItem text="Live interactive classes with recordings" />
              <ListItem text="Strong emphasis on speaking & listening" />
              <ListItem text="Complete DELE exam guidance" />
            </ul>
          </section>

          {/* COMPARISON */}
          <section>
            <h2 className={`text-3xl font-bold mb-6 ${PRIMARY_GRADIENT}`}>
              Spanish Language: Difficulty Overview
            </h2>

            <div className="grid md:grid-cols-2 gap-4">
              <Comparison
                label="Speaking"
                value="Easy – Clear pronunciation & flow"
              />
              <Comparison
                label="Grammar"
                value="Simple – Consistent and logical rules"
              />
              <Comparison
                label="Writing"
                value="Easy-Moderate – Structured sentences"
              />
              <Comparison
                label="Overall Difficulty"
                value="Easy – Ideal for new learners"
              />
            </div>
          </section>

          {/* FAQ */}
          <section>
            <h2 className={`text-3xl font-bold mb-6 ${PRIMARY_GRADIENT}`}>
              Frequently Asked Questions
            </h2>

            <div className="space-y-3">
              {FAQ_DATA.map((faq, index) => (
                <div
                  key={index}
                  className="bg-white rounded-xl border border-gray-200 overflow-hidden shadow-sm hover:shadow-md transition-shadow"
                >
                  <button
                    onClick={() => toggleFaq(index)}
                    className="w-full flex items-center justify-between p-5 text-left hover:bg-gray-50"
                  >
                    <h4 className="font-semibold pr-4">{faq.question}</h4>
                    <FaChevronDown
                      className={`text-primary transition-transform ${
                        expandedFaq === index ? "rotate-180" : ""
                      }`}
                    />
                  </button>
                  {expandedFaq === index && (
                    <div className="px-5 pb-5 pt-2 bg-gray-50 border-t">
                      <p className="text-sm text-gray-700">{faq.answer}</p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </section>
        </main>

        {/* ================= RIGHT STICKY FORM ================= */}
        <aside className="relative">
          <div ref={formRef} className="hidden lg:block sticky top-24">
            <div className="bg-white border rounded-2xl shadow-xl p-6">
              <div className="text-center mb-4">
                <h3 className={`text-2xl font-bold ${PRIMARY_GRADIENT}`}>
                  Spanish Language Program
                </h3>
                <p className="text-sm text-gray-600 mt-2">
                  DELE Prep · Live Classes · Certification Support
                </p>
              </div>

              <div className="text-center mb-6 py-4 bg-gradient-to-r from-blue-50 to-purple-50 rounded-xl">
                <div className="text-4xl font-extrabold text-primary">
                  ₹60,000/-
                </div>
                <div className="text-sm text-gray-400 line-through">
                  ₹70,000/-
                </div>
                <div className="mt-2 text-xs text-gray-600">
                  Weekday & Weekend Batches Available
                </div>
              </div>

              <form className="space-y-4">
                <input
                  className="w-full bg-gray-50 border rounded-lg py-3 px-4 text-sm"
                  placeholder="Full Name"
                />
                <input
                  className="w-full bg-gray-50 border rounded-lg py-3 px-4 text-sm"
                  placeholder="Email Address"
                />
                <input
                  className="w-full bg-gray-50 border rounded-lg py-3 px-4 text-sm"
                  placeholder="Mobile Number"
                />
                <button className="w-full py-3 rounded-lg text-white font-semibold bg-gradient-to-r from-primary-soft via-primary to-primary-soft">
                  Enroll Now
                </button>
              </form>

              <div className="mt-5 text-xs text-center text-gray-600">
                <div className="flex justify-center items-center gap-2">
                  <FaCheckCircle className="text-green-500" />
                  30-Day Money-Back Guarantee
                </div>
              </div>

              <div className="mt-6 text-center pt-4 border-t">
                <a
                  href="tel:+919123408959"
                  className="inline-flex items-center gap-2 text-sm bg-blue-50 px-4 py-2 rounded-lg text-primary font-semibold"
                >
                  <FaPhone /> +91-9123408959
                </a>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}

/* ================= SMALL COMPONENTS ================= */

function LevelItem({ title, text }) {
  return (
    <div className="bg-gradient-to-br from-slate-100 to-slate-200 rounded-xl p-6 shadow-md">
      <h4 className="font-bold mb-3">{title}</h4>
      <p className="text-sm text-gray-700">{text}</p>
    </div>
  );
}

function ListItem({ text }) {
  return (
    <li className="flex gap-2">
      <FaCheckCircle className="text-primary mt-1" />
      {text}
    </li>
  );
}

function Comparison({ label, value }) {
  return (
    <div className="bg-gradient-to-br from-slate-100 to-gray-200 rounded-xl p-6 shadow-md">
      <strong className="block mb-2">{label}</strong>
      <div className="text-sm text-gray-600">{value}</div>
    </div>
  );
}
